// Legal documents shown by the "Terms & Privacy" button (LegalDocumentsButton).
// GENERATED from the owner's Word files (ConnectYid_Privacy_Policy.docx,
// ConnectYid_Terms_of_Service.docx, CONNECTYID_DATA_PROCESSING_ADDENDUM.docx,
// received 2026-10-20). The text is legal wording: replace it from the
// owner's updated files rather than editing it by hand. English only.

export type LegalBlock = { type: "h" | "p" | "li"; text: string };
export type LegalDocument = { title: string; subtitle: string; blocks: LegalBlock[] };
export type LegalDocumentKey = "privacy" | "terms" | "dpa";

export const LEGAL_DOCUMENTS: Record<LegalDocumentKey, LegalDocument> = {
  "privacy": {
    "title": "CONNECTYID",
    "subtitle": "Privacy Policy",
    "blocks": [
      {
        "type": "p",
        "text": "Operated by Teluktu Inc. | Effective October 11, 2026"
      },
      {
        "type": "h",
        "text": "1. Scope"
      },
      {
        "type": "p",
        "text": "This Policy explains how Teluktu Inc., operating ConnectYid, collects, uses, discloses, retains, and protects personal information through its websites, applications, platform, support channels, and related services. It does not govern an Organization’s independent outreach practices or third-party services."
      },
      {
        "type": "h",
        "text": "2. Our Roles"
      },
      {
        "type": "p",
        "text": "For personal information in an Organization workspace, the Organization ordinarily determines why and how the personal information is processed and acts as controller; ConnectYid ordinarily acts as processor and processes that information only on the Organization’s documented instructions, subject to the applicable data-processing agreement and law. ConnectYid acts as an independent controller for account registration, billing, support, security, service administration, abuse prevention, service analytics, legal compliance, and its own business records. Where ConnectYid and an Organization jointly determine purposes and means, they will allocate their respective responsibilities transparently as required by applicable law. Individuals should contact the relevant Organization for Organization-controlled outreach records and ConnectYid for processing for which ConnectYid is controller."
      },
      {
        "type": "h",
        "text": "3. Information We Collect"
      },
      {
        "type": "p",
        "text": "We collect: (a) account and identity information, including name, business contact details, login credentials, Organization, role, permissions, and authentication records; (b) Organization workspace information, including contact details, physical addresses, outreach assignments, contact history, communication preferences, suppression status, follow-up notes, and administrator instructions; (c) subscription, transaction, and billing records, while payment-card details are submitted directly to Stripe and are not stored on ConnectYid servers; (d) device, log, security, and technical information, including IP address, device and browser type, timestamps, authentication activity, diagnostic data, and security events; (e) support communications and feedback; (f) integration data selected by an Organization; and (g) location data described below. CRM integrations are limited to the contact and outreach fields the Organization authorizes and that are reasonably necessary for the enabled integration. Broad CRM records, including financial contributions, detailed donor histories, and unrelated organizational files, must not be imported into ConnectYid."
      },
      {
        "type": "p",
        "text": "Location data. If a user affirmatively enables device-location permission and chooses a maps, navigation, or route-planning function, ConnectYid may access precise device location only while that function is in use. Location is used solely to provide the selected mapping, navigation, or route-planning function, is not used for advertising, and is not sold. ConnectYid does not collect device location while the application is not in use. An Organization-provided address may be geocoded into coordinates for mapping and route planning. Users may disable device-location permission through device settings, although location-based functions may then be unavailable."
      },
      {
        "type": "h",
        "text": "4. Sources"
      },
      {
        "type": "p",
        "text": "Information may come directly from users and Contacts; participating Organizations and their personnel; authorized CRM, identity, or communications integrations; devices and use of the Service; service providers; and public or lawful sources when an Organization directs or authorizes collection."
      },
      {
        "type": "h",
        "text": "5. Purposes"
      },
      {
        "type": "p",
        "text": "We process personal information for the following purposes and legal bases when GDPR or UK GDPR applies: (a) to create and administer user accounts, provide requested platform functions, process subscriptions, and deliver support, where necessary to perform a contract or take requested pre-contract steps; (b) to secure, troubleshoot, maintain, and improve the Service, prevent fraud and misuse, administer Organizations and integrations, and establish, exercise, or defend legal claims, based on ConnectYid’s or an Organization’s legitimate interests, after balancing those interests against individual rights; (c) to comply with legal, regulatory, accounting, security, and law-enforcement obligations; and (d) where required, on consent, including consent for optional device location, optional technologies, or processing for which no other lawful basis applies. Consent may be withdrawn at any time without affecting processing already undertaken lawfully. ConnectYid does not use personal information for a materially incompatible purpose without providing required notice and establishing an appropriate lawful basis."
      },
      {
        "type": "h",
        "text": "6. GDPR Legal Bases and Special Categories"
      },
      {
        "type": "p",
        "text": "Where GDPR or UK GDPR applies, each Organization is responsible, as controller, for identifying, documenting, and maintaining an appropriate lawful basis under Article 6 and, where Customer Data reveals or permits an inference regarding religious beliefs or otherwise constitutes special-category data, an applicable condition under Article 9. Depending on the Organization’s circumstances, an Article 9 condition may include Article 9(2)(d) for qualifying processing by a not-for-profit body with a religious aim involving its members, former members, or persons having regular contact with it in connection with its purposes, subject to the conditions and safeguards of that provision, or explicit consent where legally appropriate. Organizations must not rely on Article 9(2)(d) for persons or disclosures outside its scope and must obtain any consent required for disclosure outside the qualifying body."
      },
      {
        "type": "p",
        "text": "For Organization-controlled Customer Data, ConnectYid acts ordinarily as processor and processes such data only on the Organization’s documented instructions and as otherwise permitted or required by applicable law. Where ConnectYid independently acts as controller, ConnectYid will identify and maintain the lawful basis and, where applicable, special-category condition required for its processing."
      },
      {
        "type": "h",
        "text": "7. Disclosures"
      },
      {
        "type": "p",
        "text": "We disclose personal information only as necessary to: (a) the relevant Organization and its authorized personnel; (b) contracted hosting, infrastructure, authentication, communications, support, security, payment, mapping, error-monitoring, integration, and professional-service providers acting under appropriate contractual and confidentiality restrictions; (c) integration providers at the Organization’s documented direction; (d) authorities or other parties where disclosure is required by law or necessary to establish, exercise, or defend legal claims or protect rights, safety, and system integrity; (e) transaction counterparties under confidentiality and appropriate safeguards; and (f) other recipients at the individual’s direction or with valid consent. We do not sell personal information or Customer Data and do not share it for advertising. Current service-provider functions include Google Maps Platform for geocoding, maps, navigation, and enabled device-location features; Stripe for subscriptions and payments; Pusher for real-time in-app notifications; Sentry for technical error reporting; and ChabadOne or Salesforce only when an Organization enables an integration. ConnectYid requires processors to act only on documented instructions, protect confidentiality and security, assist with rights and incident obligations, control subprocessors, return or delete data at the end of services, and provide information needed to demonstrate compliance."
      },
      {
        "type": "h",
        "text": "8. California Notice at Collection"
      },
      {
        "type": "p",
        "text": "We may collect California categories including identifiers, customer-record information, internet or network activity, geolocation information that may include precise geolocation when a user affirmatively enables a device-location feature, professional or organizational information, and inferences from outreach context. Sensitive personal information may include account credentials, precise geolocation, and information that reveals or permits an inference regarding religious beliefs. We collect, use, retain, and disclose these categories for the business and operational purposes described in Sections 5, 7, and 11. We do not sell personal information or share it for cross-context behavioral advertising. We do not use or disclose sensitive personal information for purposes that require a right to limit under California law unless this Policy and the required choice mechanism are updated first."
      },
      {
        "type": "h",
        "text": "9. California Rights"
      },
      {
        "type": "p",
        "text": "Subject to applicability, verification, and legal exceptions, California residents may request access or knowledge, deletion, correction, and portability; opt out of sale or sharing; limit qualifying uses of sensitive personal information; use an authorized agent; and receive non-discriminatory treatment. We may deny or limit a request where permitted, including when we cannot verify identity or authority, must retain information for security or legal reasons, or the request concerns information ConnectYid processes only for an Organization. If CCPA thresholds do not apply, ConnectYid may honor reasonable requests voluntarily, but doing so does not waive an applicable exemption or create a continuing obligation beyond applicable law."
      },
      {
        "type": "h",
        "text": "10. GDPR and Other Rights"
      },
      {
        "type": "p",
        "text": "Subject to applicable law, individuals may request access, rectification, erasure, restriction, and portability; object to processing based on legitimate interests; object at any time to direct marketing; withdraw consent; and lodge a complaint with the competent supervisory authority. Where information is processed for an Organization, requests should ordinarily be directed to that Organization; ConnectYid will provide required assistance as processor. ConnectYid does not make decisions producing legal or similarly significant effects solely through automated processing. Rights may be limited by applicable exemptions, and ConnectYid will explain a denial where required. Requests are ordinarily answered within one month, subject to legally permitted extensions for complex or numerous requests."
      },
      {
        "type": "h",
        "text": "11. Retention"
      },
      {
        "type": "p",
        "text": "We retain personal information only as long as reasonably necessary for the documented purposes, account administration, security, support, dispute resolution, legal compliance, enforcement, and fraud or abuse prevention. Organization workspace data is generally retained during the active subscription and for up to 30 days after termination for export or reactivation, unless the applicable agreement or documented Organization instruction provides a shorter period. After that period, Customer Data is deleted or de-identified, subject to legal holds, required record retention, and backup cycles. Backup copies are isolated from ordinary use and deleted or overwritten in accordance with the applicable backup schedule. Account, billing, security, and legal records may be retained for the period reasonably necessary to satisfy legal obligations, resolve disputes, and enforce agreements. ConnectYid periodically reviews retention and securely deletes, anonymizes, or returns data when retention is no longer justified. An Organization may request deletion at any time, subject to its authority, applicable law, and valid retention exceptions."
      },
      {
        "type": "h",
        "text": "12. International Transfers"
      },
      {
        "type": "p",
        "text": "Personal information may be processed in the United States and other countries where ConnectYid or its service providers operate. Before making a restricted transfer from the EEA, UK, or Switzerland, ConnectYid will implement a lawful transfer mechanism, as applicable, such as an adequacy decision, the European Commission Standard Contractual Clauses, the UK International Data Transfer Agreement or UK Addendum, or another legally permitted mechanism. ConnectYid will conduct and document any required transfer-risk assessment, implement supplementary technical, contractual, or organizational measures where necessary, and make information concerning the applicable safeguards available on request. ConnectYid will not make or direct a restricted international transfer unless an appropriate mechanism and related documentation are in place."
      },
      {
        "type": "p",
        "text": "If Article 27 of the EU GDPR or the corresponding UK representative requirement applies, ConnectYid will appoint the required representative before intentionally offering the Service to affected individuals or monitoring their behavior and will publish the representative’s name and contact details in this Policy. ConnectYid will not rely on this Policy as a substitute for making that appointment."
      },
      {
        "type": "h",
        "text": "13. Security and Breaches"
      },
      {
        "type": "p",
        "text": "We use risk-appropriate administrative, technical, and physical safeguards designed to protect personal information, including access controls, authentication, logging, vendor controls, secure-development practices, backups, encryption in transit, logical workspace separation, administrator-managed roles and permissions, and hashed password storage. Access is limited to persons with an authorized need and subject to confidentiality obligations. We maintain processes to assess vendors, address vulnerabilities, restore availability, test safeguards, and review effectiveness. No system is completely secure. ConnectYid will investigate, mitigate, and document personal-data breaches; notify affected controllers without undue delay after becoming aware of a breach involving Customer Data; and, when ConnectYid is controller, notify the competent supervisory authority within the legally required period and affected individuals when required."
      },
      {
        "type": "p",
        "text": "ConnectYid applies data protection by design and by default, limits access and collection to what is necessary for each purpose, maintains records of processing where required, and conducts a data-protection impact assessment before undertaking processing likely to result in high risk. ConnectYid will designate a data protection officer if legally required and otherwise assign responsibility for privacy compliance."
      },
      {
        "type": "h",
        "text": "14. Cookies and Similar Technologies"
      },
      {
        "type": "p",
        "text": "ConnectYid uses only essential cookies and browser storage necessary for account authentication, session security, sign-in, load balancing, and core Service functions, including security/session tokens and related sign-in information. ConnectYid does not use advertising cookies, analytics trackers, third-party tracking pixels, or technologies to track users across unrelated websites. If ConnectYid later introduces a non-essential technology, it will provide notice and obtain prior consent where required before activating it."
      },
      {
        "type": "h",
        "text": "15. Communications"
      },
      {
        "type": "p",
        "text": "Users may opt out of promotional email through the message mechanism or by contacting us. Necessary account, service, legal, and security notices may continue. Organization-directed outreach preferences must be exercised with the relevant Organization or sender."
      },
      {
        "type": "h",
        "text": "16. Children"
      },
      {
        "type": "p",
        "text": "The Service is not directed to children, and account registration is limited to individuals aged 18 or older. Organizations must not enter a child’s personal information unless the processing is lawful, necessary for an authorized youth program, covered by an appropriate Article 6 basis and any applicable Article 9 condition, supported by required notices and parental or guardian authorization, and permitted by the Organization’s written policies. Organizations remain responsible for applicable age-of-consent and child-protection requirements. ConnectYid does not knowingly seek children’s information for its own purposes. If ConnectYid learns that a child’s information was processed unlawfully, it will restrict and delete the information or return it to the controlling Organization as required by law. Inquiries may be sent to connectyid1@gmail.com."
      },
      {
        "type": "h",
        "text": "17. Third-Party Services"
      },
      {
        "type": "p",
        "text": "Integrations and linked services have independent terms and privacy practices. Organizations must evaluate their providers’ lawful bases, terms, roles, safeguards, transfer mechanisms, subprocessors, and retention settings before enabling them. ChabadOne and Salesforce integrations are opt-in and may import only the fields affirmatively authorized by the Organization into its ConnectYid workspace. ConnectYid will not export or synchronize Customer Data back to a third-party CRM unless the Organization expressly enables that direction and the applicable data-processing and transfer documentation covers it. Disabling an integration stops future synchronization but does not automatically delete copies previously lawfully transferred; deletion requests must be directed to each applicable controller."
      },
      {
        "type": "h",
        "text": "18. Requests and Appeals"
      },
      {
        "type": "p",
        "text": "Submit requests to connectyid1@gmail.com and identify the right, account, Organization, and record involved. ConnectYid may request information reasonably necessary to verify identity, residency, and authority, but will not request disproportionate information. Where ConnectYid is controller, it will respond within the period required by applicable law and provide information about any extension, refusal, fee, or appeal right. Where ConnectYid is processor, it will promptly refer the request to the applicable Organization and provide required assistance. EEA individuals may complain to the supervisory authority in their habitual residence, place of work, or place of the alleged infringement. UK individuals may complain to the UK Information Commissioner’s Office. Individuals may contact ConnectYid first, but are not required to do so."
      },
      {
        "type": "h",
        "text": "19. Changes"
      },
      {
        "type": "p",
        "text": "We may update this Policy to reflect changes in the Service, law, security needs, or practices. We will post the revised version with a new effective date and provide additional notice of material changes or obtain consent where required. Prior versions may be requested at connectyid1@gmail.com."
      },
      {
        "type": "p",
        "text": "Material changes affecting EEA or UK processing will be communicated before taking effect where required. If a change requires consent, ConnectYid will obtain consent rather than relying solely on continued use."
      },
      {
        "type": "h",
        "text": "20. Contact"
      },
      {
        "type": "p",
        "text": "Teluktu Inc. (ConnectYid)"
      },
      {
        "type": "p",
        "text": "Attn: Legal & Privacy Department"
      },
      {
        "type": "p",
        "text": "221 Paseo del Pueblo Norte"
      },
      {
        "type": "p",
        "text": "Taos, New Mexico 87571"
      },
      {
        "type": "p",
        "text": "General & Legal Inquiries: connectyid1@gmail.com"
      },
      {
        "type": "p",
        "text": "Privacy Requests & Data Subject Inquiries: connectyid1@gmail.com"
      },
      {
        "type": "p",
        "text": "EU/UK Representative & Appeals: connectyid1@gmail.com"
      },
      {
        "type": "p",
        "text": "EU and UK representatives: If legally required, the appointed representative’s identity and contact details will be published here before the relevant processing begins. Data-protection contact: connectyid1@gmail.com"
      }
    ]
  },
  "terms": {
    "title": "CONNECTYID",
    "subtitle": "Terms of Service",
    "blocks": [
      {
        "type": "p",
        "text": "Operated by Teluktu Inc. | Effective October 11, 2026"
      },
      {
        "type": "h",
        "text": "1. Agreement"
      },
      {
        "type": "p",
        "text": "Welcome to ConnectYid. ConnectYid is built to support the sacred outreach and community-building efforts of Shluchim, Anash, volunteers, and participating Jewish organizations. These Terms govern access to and use of ConnectYid websites, applications, software, integrations, and related services (the “Service”). They form a binding agreement between you and Teluktu Inc. (“ConnectYid,” “we,” or “us”). If you use the Service on behalf of an Organization, you represent and warrant that you have authority to bind that Organization to these Terms."
      },
      {
        "type": "h",
        "text": "2. Service"
      },
      {
        "type": "p",
        "text": "ConnectYid is an outreach-coordination and relationship-management platform for participating Jewish communities and organizations. It may provide organization workspaces, contact records, outreach notes, assignments, follow-up tools, mobile access, and synchronization with authorized third-party CRM systems."
      },
      {
        "type": "h",
        "text": "3. Organizations and Users"
      },
      {
        "type": "p",
        "text": "An “Organization” administers a workspace; an “Administrator” manages it; an “Authorized User” is permitted to use it; and a “Contact” is a person whose information is processed. Organizations control user permissions and are responsible for lawful outreach, notices, consents, communication preferences, and instructions."
      },
      {
        "type": "h",
        "text": "4. Eligibility and Accounts"
      },
      {
        "type": "p",
        "text": "Account users must be at least 18 years old, provide accurate registration details, safeguard credentials, avoid credential sharing, and promptly report suspected unauthorized access to legal@connectyid.org. Organizations are fully responsible for managing user access and workspace permissions for their personnel and volunteers. Organizations must immediately revoke account access upon a volunteer’s or staff member’s departure or role change. Authorized Users and volunteers are strictly prohibited from exporting, downloading, or storing unmanaged local spreadsheet copies of Customer Data on personal devices without express written approval from the Organization."
      },
      {
        "type": "h",
        "text": "5. Customer Data"
      },
      {
        "type": "p",
        "text": "“Customer Data” means information submitted to or processed through an Organization workspace. The Organization retains its rights in Customer Data. The Organization grants ConnectYid a nonexclusive, worldwide, royalty-free license to host, copy, transmit, display, back up, secure, and otherwise process Customer Data solely as reasonably necessary to operate, maintain, protect, support, and improve the Service; provide enabled integrations; follow documented instructions; and comply with law. ConnectYid may generate and use aggregated or de-identified information that does not reasonably identify an individual or Organization for security, analytics, service improvement, and reporting. ConnectYid does not acquire ownership of identifiable Customer Data."
      },
      {
        "type": "p",
        "text": "No sale or resale of Customer Data. ConnectYid does not sell, resell, rent, broker or otherwise monetize identifiable Customer Data, and does not disclose it to third parties for advertising. ConnectYid may share Customer Data only with service providers necessary to operate the Service, with integrations authorized by the Organization, or where legally required, as further described in the Privacy Policy and subject to applicable protections."
      },
      {
        "type": "h",
        "text": "6. Privacy Roles"
      },
      {
        "type": "p",
        "text": "For Organization-controlled Customer Data, the Organization ordinarily acts as controller or business and ConnectYid as processor, service provider, or contractor. ConnectYid acts as controller or business for account, support, security, service-analytics, and legal-compliance information as described in the Privacy Policy."
      },
      {
        "type": "h",
        "text": "7. Data Minimization and Restricted Data"
      },
      {
        "type": "p",
        "text": "Users may enter only information reasonably necessary for legitimate, authorized outreach (such as name, phone, email, physical address, and basic outreach status notes). Without ConnectYid’s prior written approval, users shall not submit government identifiers, payment card data, health records, biometric information, or high-risk identifiers. Because outreach context may reveal or permit inferences regarding religious beliefs—constituting special category data under GDPR and sensitive personal information under applicable state laws—Organizations must maintain strict role-based access, train volunteers, keep notes strictly limited to outreach logistics, and establish an applicable legal condition (such as GDPR Article 9(2)(d) for religious non-profits or explicit consent). ConnectYid reserves the right to implement automated safety filtering and remove or restrict data that creates an unreasonable security, legal, or privacy risk."
      },
      {
        "type": "h",
        "text": "8. Integrations"
      },
      {
        "type": "p",
        "text": "An Organization may enable integrations with authorized third-party CRM systems. Data synchronization is strictly limited to fields reasonably necessary for outreach coordination (such as contact info, assigned volunteer, and follow-up status). Broader institutional CRM records—including financial contributions, detailed donor histories, and full organizational files—must remain within the primary third-party CRM and shall not be imported into or processed by ConnectYid. ConnectYid is not responsible for third-party practices, and disabling an integration will stop future syncing but will not automatically delete records previously transferred to the CRM prior to disconnection."
      },
      {
        "type": "h",
        "text": "9. Lawful Communications"
      },
      {
        "type": "p",
        "text": "Organizations and users are solely responsible for the content, recipients, timing, and lawfulness of their outreach; obtaining required permissions and consents; maintaining suppression lists; and honoring opt-outs. They must comply with applicable privacy, anti-spam, telemarketing, text-messaging, consumer-protection, and recording laws. ConnectYid does not supply legal consent, verify that a Contact may lawfully be contacted, or act as the sender of Organization communications merely by providing the Service."
      },
      {
        "type": "p",
        "text": "Account registration is restricted to individuals aged 18 and older. Where an Organization conducts youth-oriented outreach (such as Hebrew schools, youth clubs, or community programs), the Organization represents and warrants that it possesses documented legal authority, notice, and valid parental/guardian consent before entering any minor’s personal information into the Service. Direct messaging or digital communication to minors via the Service is strictly prohibited unless explicitly authorized under the Organization’s written policies and compliant with applicable child privacy laws."
      },
      {
        "type": "h",
        "text": "10. Acceptable Use"
      },
      {
        "type": "p",
        "text": "Users may not violate law or another person’s rights; harass, threaten, discriminate against, shame, exploit, or deceive; use information outside authorized community-outreach purposes; send unlawful unsolicited communications; introduce malware; test or bypass security without written authorization; access another account or workspace without permission; scrape or export data outside authorized features; reverse engineer except where law prohibits restriction; resell the Service without permission; or use the Service or its output to build a competing product. ConnectYid may investigate suspected misuse and preserve or disclose relevant information where reasonably necessary to protect the Service, users, Contacts, or third parties or to comply with law."
      },
      {
        "type": "h",
        "text": "11. Administration and Requests"
      },
      {
        "type": "p",
        "text": "Administrators may access, export, correct, restrict, or delete workspace data and remove users. Requests concerning Organization-controlled data should be directed to the Organization. ConnectYid may refer such requests to the Organization and provide reasonable assistance."
      },
      {
        "type": "h",
        "text": "12. Intellectual Property"
      },
      {
        "type": "p",
        "text": "ConnectYid and its licensors own the Service and related intellectual property, excluding Customer Data. ConnectYid grants Authorized Users a limited, nonexclusive, nontransferable, revocable right to use the Service for lawful internal purposes. ConnectYid may use feedback without identifying its source publicly."
      },
      {
        "type": "h",
        "text": "13. Fees"
      },
      {
        "type": "p",
        "text": "Any fees, billing cycles, usage limits, renewals, and taxes will be stated in an order form or purchase flow. ConnectYid will not charge for a previously free period unless the user affirmatively accepts applicable pricing."
      },
      {
        "type": "h",
        "text": "14. Service Changes and Availability"
      },
      {
        "type": "p",
        "text": "ConnectYid may add, modify, limit, suspend, or discontinue features for security, legal, operational, funding, or community needs. ConnectYid does not promise that any feature or the Service will remain available. If a paid order applies, ConnectYid will use commercially reasonable efforts not to materially reduce core paid functionality during the applicable term. Any support, service-level, data-backup, or uptime commitment must appear in a separate written agreement."
      },
      {
        "type": "h",
        "text": "15. Security and Incidents"
      },
      {
        "type": "p",
        "text": "ConnectYid will maintain reasonable administrative, technical, and physical safeguards appropriate to the nature of the information, the Service, and the risk. No method of transmission or storage is completely secure, and ConnectYid does not warrant absolute security. ConnectYid will notify affected Organizations of a confirmed breach of Customer Data without undue delay and provide reasonably available information needed for legally required notices. Organizations remain responsible for user access, devices, credentials, integrations, and promptly removing access that is no longer authorized."
      },
      {
        "type": "h",
        "text": "16. Termination and Data Export"
      },
      {
        "type": "p",
        "text": "A user may stop using the Service at any time. ConnectYid may suspend, restrict, or terminate access if it reasonably believes there is a material breach, security or privacy risk, unlawful activity, misuse of community data, nonpayment under a paid order, or risk of harm to the Service or others. When reasonably practicable and consistent with safety and law, ConnectYid will provide notice and an opportunity to cure. In addition, ConnectYid may terminate access for any reason or no reason at any time upon the provision of 90 days prior written notice. Organizations may request a Customer Data export during active use and for 30 days after termination. ConnectYid may thereafter delete data subject to backup cycles, legal holds, fraud prevention, and other legal obligations."
      },
      {
        "type": "p",
        "text": "An Organization may request deletion of its account and its Customer Data at any time, subject to lawful retention requirements and the practical limitations described in the Privacy Policy. After termination, ConnectYid may temporarily retain data for a limited reactivation/export period before deletion or de-identification, subject to required backup and legal exceptions."
      },
      {
        "type": "h",
        "text": "17. Disclaimers"
      },
      {
        "type": "p",
        "text": "TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, OR NON-INFRINGEMENT. CONNECTYID DOES NOT WARRANT UNINTERRUPTED OR ERROR-FREE OPERATION, THE ACCURACY OR COMPLETENESS OF CONTACT OR OUTREACH INFORMATION, THE SUCCESS OF ANY OUTREACH, OR THAT DATA WILL NEVER BE LOST OR ACCESSED WITHOUT AUTHORIZATION. THE SERVICE IS NOT AN EMERGENCY, MEDICAL, CHILD-SUPERVISION, OR SAFETY-CRITICAL COMMUNICATION SYSTEM. THESE DISCLAIMERS DO NOT LIMIT RIGHTS THAT CANNOT LAWFULLY BE WAIVED."
      },
      {
        "type": "h",
        "text": "18. Limitation of Liability"
      },
      {
        "type": "p",
        "text": "TO THE MAXIMUM EXTENT PERMITTED BY LAW, CONNECTYID AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, VOLUNTEERS, AND SERVICE PROVIDERS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL DAMAGES, OR FOR LOST PROFITS, REVENUE, GOODWILL, USE, OR DATA, ARISING FROM OR RELATING TO THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY. CONNECTYID’S AGGREGATE LIABILITY WILL NOT EXCEED THE GREATER OF (A) AMOUNTS PAID BY THE CLAIMANT FOR THE SERVICE DURING THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO LIABILITY OR (B) US$100. These limitations do not apply to ConnectYid’s fraud or willful misconduct, or liability that applicable law prohibits limiting."
      },
      {
        "type": "h",
        "text": "19. Indemnification"
      },
      {
        "type": "p",
        "text": "Each Organization will defend, indemnify, and hold harmless ConnectYid and its affiliates, officers, directors, employees, and volunteers from third-party claims, damages, judgments, penalties, and reasonable legal fees arising from: (a) Customer Data supplied or directed by the Organization; (b) the Organization’s or its users’ outreach, communications, or relationship with a Contact; (c) failure to obtain required notices, permissions, or consents or honor an opt-out; (d) unlawful or unauthorized use of the Service; or (e) a material breach of these Terms. This obligation does not apply to the extent a claim results from ConnectYid’s gross negligence, willful misconduct, or breach of these Terms. ConnectYid will provide prompt notice, permit the Organization to control the defense with reasonably acceptable counsel, and provide reasonable cooperation at the Organization’s expense. No settlement may admit fault by or impose a nonmonetary obligation on ConnectYid without its consent."
      },
      {
        "type": "h",
        "text": "20. Confidentiality"
      },
      {
        "type": "p",
        "text": "Each party will use the other’s nonpublic confidential information only as permitted under this agreement and protect it with reasonable care. Information independently developed, lawfully received without restriction, or public without breach is excluded. Legally compelled disclosure is permitted after notice when allowed."
      },
      {
        "type": "h",
        "text": "21. Informal Resolution"
      },
      {
        "type": "p",
        "text": "Before filing a claim, each party will provide written notice describing the dispute and allow 30 days for good-faith informal resolution. This requirement does not prevent either party from seeking urgent injunctive relief to protect confidential information, personal information, intellectual property, or system security."
      },
      {
        "type": "h",
        "text": "22. Governing Law"
      },
      {
        "type": "p",
        "text": "New Mexico law governs, excluding conflicts rules. State and federal courts serving Taos County, New Mexico have exclusive jurisdiction, and each party waives objections based on venue or inconvenient forum to the extent permitted by law."
      },
      {
        "type": "h",
        "text": "23. Changes"
      },
      {
        "type": "p",
        "text": "ConnectYid may revise these Terms to reflect changes in the Service, law, security needs, or operating practices. Material changes will be communicated by reasonable means before they take effect where required by law. Continued use after the effective date constitutes acceptance, except where applicable law requires affirmative consent. If an Organization does not agree, its remedy is to stop using the Service before the revised Terms take effect."
      },
      {
        "type": "h",
        "text": "24. General"
      },
      {
        "type": "p",
        "text": "These Terms, the Privacy Policy, any data processing addendum, and any order form are the entire agreement. An order form controls for that order. Invalid terms are limited to the minimum necessary. Assignment is restricted except in a merger or sale of substantially all assets. No agency, partnership, employment, fiduciary, or joint venture is created."
      },
      {
        "type": "h",
        "text": "25. Contact"
      },
      {
        "type": "p",
        "text": "Teluktu Inc. (ConnectYid)\n221 Paseo del Pueblo Norte\nTaos, New Mexico 87571\nLegal, privacy, and security: legal@connectyid.org"
      }
    ]
  },
  "dpa": {
    "title": "CONNECTYID DATA PROCESSING ADDENDUM",
    "subtitle": "GDPR AND UK GDPR",
    "blocks": [
      {
        "type": "p",
        "text": "Effective Date: The date the Organization accepts this Data Processing Addendum electronically."
      },
      {
        "type": "p",
        "text": "This Data Processing Addendum (the “DPA”) is entered into between Teluktu Inc., doing business as ConnectYid (“ConnectYid”), and the Organization accepting this DPA (“Organization”)."
      },
      {
        "type": "p",
        "text": "This DPA supplements and forms part of the ConnectYid Terms of Service (the “Terms”). The Organization acknowledges that it has accepted the Terms and the ConnectYid Privacy Policy (the “Privacy Policy”)."
      },
      {
        "type": "p",
        "text": "By clicking “I Accept,” “Agree,” or a substantially similar electronic acceptance mechanism, the individual accepting this DPA represents that such individual has authority to bind the Organization, and the Organization agrees to this DPA."
      },
      {
        "type": "h",
        "text": "1. Definitions"
      },
      {
        "type": "p",
        "text": "Capitalized terms not defined in this DPA have the meanings given in the Terms or Privacy Policy."
      },
      {
        "type": "p",
        "text": "“Applicable Data Protection Law” means, to the extent applicable to processing under this DPA, Regulation (EU) 2016/679 (“GDPR”), the GDPR as incorporated into United Kingdom law (“UK GDPR”), the UK Data Protection Act 2018, and applicable implementing or supplementary data-protection legislation."
      },
      {
        "type": "p",
        "text": "“Controller,” “Processor,” “Data Subject,” “Personal Data,” “Personal Data Breach,” “Processing,” and “Special Category Data” have the meanings assigned under Applicable Data Protection Law."
      },
      {
        "type": "p",
        "text": "“Customer Data” has the meaning assigned in the Terms."
      },
      {
        "type": "p",
        "text": "“Subprocessor” means a third party engaged by ConnectYid to Process Personal Data on behalf of the Organization."
      },
      {
        "type": "h",
        "text": "2. Relationship of the Parties"
      },
      {
        "type": "p",
        "text": "For Customer Data Processed through an Organization workspace, the Organization ordinarily acts as Controller and ConnectYid ordinarily acts as Processor."
      },
      {
        "type": "p",
        "text": "ConnectYid will Process Personal Data on behalf of the Organization only on the Organization’s documented instructions, including the instructions embodied in the Terms, Privacy Policy, this DPA, the Organization’s configuration and use of the Service, enabled integrations, and other documented instructions accepted by ConnectYid, except where Applicable Data Protection Law requires otherwise."
      },
      {
        "type": "p",
        "text": "ConnectYid acts as an independent Controller for information Processed for its own account-registration, billing, support, security, service-administration, abuse-prevention, service-analytics, legal-compliance, and business-record purposes, as described in the Privacy Policy."
      },
      {
        "type": "h",
        "text": "3. Processing Instructions"
      },
      {
        "type": "p",
        "text": "The Organization instructs ConnectYid to Process Personal Data as reasonably necessary to:"
      },
      {
        "type": "p",
        "text": "(a) provide and administer the Service;"
      },
      {
        "type": "p",
        "text": "(b) host, transmit, organize, secure, back up, retrieve, and otherwise Process Customer Data;"
      },
      {
        "type": "p",
        "text": "(c) authenticate Authorized Users and administer permissions;"
      },
      {
        "type": "p",
        "text": "(d) provide support and troubleshooting;"
      },
      {
        "type": "p",
        "text": "(e) provide integrations affirmatively enabled by the Organization;"
      },
      {
        "type": "p",
        "text": "(f) maintain the availability, integrity, and security of the Service; and"
      },
      {
        "type": "p",
        "text": "(g) comply with documented Organization instructions and Applicable Data Protection Law."
      },
      {
        "type": "p",
        "text": "If ConnectYid believes an instruction infringes Applicable Data Protection Law, ConnectYid will inform the Organization unless prohibited by law and may suspend the affected Processing until the parties resolve the issue."
      },
      {
        "type": "h",
        "text": "4. Organization Responsibilities and Special Category Data"
      },
      {
        "type": "p",
        "text": "The Organization is responsible for determining the purposes and means of Processing Customer Data and for establishing and documenting all lawful bases, notices, permissions, consents, and other legal conditions required for that Processing."
      },
      {
        "type": "p",
        "text": "The Organization acknowledges that Customer Data may reveal or permit an inference concerning religious beliefs and therefore may constitute Special Category Data."
      },
      {
        "type": "p",
        "text": "Where Article 9 GDPR or UK GDPR applies, the Organization represents that it has identified and documented an applicable Article 9 condition before instructing ConnectYid to Process such data."
      },
      {
        "type": "p",
        "text": "Where the Organization relies on Article 9(2)(d), the Organization is responsible for determining that the requirements and limitations of that provision are satisfied. Where explicit consent or another legal authorization is required, the Organization is responsible for obtaining and documenting it."
      },
      {
        "type": "p",
        "text": "The Organization will not instruct ConnectYid to Process Personal Data unlawfully."
      },
      {
        "type": "h",
        "text": "5. Confidentiality"
      },
      {
        "type": "p",
        "text": "ConnectYid will ensure that persons authorized to Process Personal Data on behalf of the Organization:"
      },
      {
        "type": "p",
        "text": "(a) Process such Personal Data only as authorized;"
      },
      {
        "type": "p",
        "text": "(b) are subject to appropriate confidentiality obligations; and"
      },
      {
        "type": "p",
        "text": "(c) receive appropriate privacy and security instructions relevant to their functions."
      },
      {
        "type": "h",
        "text": "6. Security Measures"
      },
      {
        "type": "p",
        "text": "Taking into account the nature, scope, context and purposes of Processing and the risks to individuals, ConnectYid will maintain appropriate administrative, technical, and organizational safeguards designed to protect Personal Data."
      },
      {
        "type": "p",
        "text": "Such safeguards include, as applicable:"
      },
      {
        "type": "li",
        "text": "encryption of data in transit;"
      },
      {
        "type": "li",
        "text": "authentication and access controls;"
      },
      {
        "type": "li",
        "text": "administrator-managed roles and permissions;"
      },
      {
        "type": "li",
        "text": "logical separation of Organization workspaces;"
      },
      {
        "type": "li",
        "text": "hashed password storage;"
      },
      {
        "type": "li",
        "text": "security logging and monitoring;"
      },
      {
        "type": "li",
        "text": "vendor controls;"
      },
      {
        "type": "li",
        "text": "secure-development practices;"
      },
      {
        "type": "li",
        "text": "backup and recovery measures;"
      },
      {
        "type": "li",
        "text": "vulnerability-management procedures; and"
      },
      {
        "type": "li",
        "text": "processes designed to restore availability and review security effectiveness."
      },
      {
        "type": "p",
        "text": "ConnectYid may update its safeguards provided that the overall level of protection is not materially diminished."
      },
      {
        "type": "h",
        "text": "7. Subprocessors"
      },
      {
        "type": "p",
        "text": "The Organization grants ConnectYid general written authorization to engage Subprocessors necessary to provide the Service."
      },
      {
        "type": "p",
        "text": "ConnectYid will:"
      },
      {
        "type": "p",
        "text": "(a) impose appropriate data-protection obligations on each Subprocessor;"
      },
      {
        "type": "p",
        "text": "(b) require each Subprocessor to provide appropriate confidentiality and security protections;"
      },
      {
        "type": "p",
        "text": "(c) require Subprocessors to Process Personal Data only for authorized purposes; and"
      },
      {
        "type": "p",
        "text": "(d) remain responsible for Subprocessor performance to the extent required by Applicable Data Protection Law."
      },
      {
        "type": "p",
        "text": "Current service-provider functions may include hosting and infrastructure, payment processing, mapping, communications, error monitoring, support, security, and Organization-enabled integrations. Current providers are identified in the Privacy Policy or other Subprocessor information made available by ConnectYid."
      },
      {
        "type": "p",
        "text": "ConnectYid will provide notice of an intended material addition or replacement of a Subprocessor where required by Applicable Data Protection Law and allow the Organization a reasonable opportunity to raise a documented data-protection objection."
      },
      {
        "type": "h",
        "text": "8. Data Subject Requests"
      },
      {
        "type": "p",
        "text": "Taking into account the nature of Processing, ConnectYid will provide reasonable assistance to enable the Organization to respond to Data Subject requests concerning Customer Data, including requests for access, rectification, erasure, restriction, portability, or objection where applicable."
      },
      {
        "type": "p",
        "text": "If ConnectYid receives such a request directly concerning Organization-controlled Customer Data, ConnectYid may refer the requester to the Organization and will not independently respond except as authorized by the Organization or required by law."
      },
      {
        "type": "h",
        "text": "9. Compliance Assistance"
      },
      {
        "type": "p",
        "text": "Taking into account the nature of Processing and information available to ConnectYid, ConnectYid will provide reasonable assistance to the Organization with obligations concerning:"
      },
      {
        "type": "p",
        "text": "(a) security of Processing;"
      },
      {
        "type": "p",
        "text": "(b) Personal Data Breach assessment and notification;"
      },
      {
        "type": "p",
        "text": "(c) data-protection impact assessments; and"
      },
      {
        "type": "p",
        "text": "(d) prior consultation with a supervisory authority,"
      },
      {
        "type": "p",
        "text": "to the extent required by Applicable Data Protection Law and relevant to ConnectYid’s Processing."
      },
      {
        "type": "h",
        "text": "10. Personal Data Breaches"
      },
      {
        "type": "p",
        "text": "ConnectYid will notify the Organization without undue delay after becoming aware of a Personal Data Breach affecting Customer Data Processed on the Organization’s behalf."
      },
      {
        "type": "p",
        "text": "As information becomes reasonably available, ConnectYid will provide information concerning:"
      },
      {
        "type": "p",
        "text": "(a) the nature of the breach;"
      },
      {
        "type": "p",
        "text": "(b) affected categories of Personal Data and Data Subjects;"
      },
      {
        "type": "p",
        "text": "(c) likely consequences;"
      },
      {
        "type": "p",
        "text": "(d) measures taken or proposed to address the breach; and"
      },
      {
        "type": "p",
        "text": "(e) information reasonably necessary for the Organization to satisfy applicable notification obligations."
      },
      {
        "type": "p",
        "text": "ConnectYid’s notification of a Personal Data Breach is not an acknowledgment of fault or liability."
      },
      {
        "type": "h",
        "text": "11. Information, Audits and Demonstration of Compliance"
      },
      {
        "type": "p",
        "text": "ConnectYid will make available information reasonably necessary to demonstrate compliance with its Processor obligations under this DPA and Applicable Data Protection Law."
      },
      {
        "type": "p",
        "text": "Where required by Applicable Data Protection Law, ConnectYid will permit and contribute to reasonable audits or inspections concerning Processing covered by this DPA."
      },
      {
        "type": "p",
        "text": "Unless Applicable Data Protection Law or a supervisory authority requires otherwise, any audit will:"
      },
      {
        "type": "p",
        "text": "(a) occur on reasonable advance notice;"
      },
      {
        "type": "p",
        "text": "(b) be conducted during normal business hours;"
      },
      {
        "type": "p",
        "text": "(c) avoid unreasonable disruption;"
      },
      {
        "type": "p",
        "text": "(d) protect the confidentiality and security of other customers and ConnectYid systems; and"
      },
      {
        "type": "p",
        "text": "(e) use existing independent audit reports, certifications, questionnaires, or comparable materials where reasonably sufficient."
      },
      {
        "type": "h",
        "text": "12. International Transfers"
      },
      {
        "type": "p",
        "text": "ConnectYid may Process Personal Data in the United States and other countries where ConnectYid or its authorized Subprocessors operate, subject to Applicable Data Protection Law."
      },
      {
        "type": "p",
        "text": "Before making a restricted transfer from the EEA or United Kingdom requiring an Article 46 safeguard, the parties will rely upon an applicable lawful transfer mechanism."
      },
      {
        "type": "p",
        "text": "For EEA restricted transfers, where required, the applicable modules of the European Commission Standard Contractual Clauses adopted under Commission Implementing Decision (EU) 2021/914 (“EU SCCs”) will apply."
      },
      {
        "type": "p",
        "text": "For UK restricted transfers, where required, the parties will use the UK International Data Transfer Agreement, the UK International Data Transfer Addendum to the EU SCCs, or another mechanism recognized under UK law."
      },
      {
        "type": "p",
        "text": "ConnectYid will conduct any legally required transfer-risk assessment and implement supplementary safeguards where required."
      },
      {
        "type": "p",
        "text": "If an adequacy determination or another lawful transfer mechanism applies to a transfer, the parties may rely upon that mechanism instead."
      },
      {
        "type": "p",
        "text": "Nothing in this DPA modifies the mandatory text of an applicable approved transfer mechanism. In the event of conflict between this DPA and mandatory terms of an applicable transfer mechanism, those mandatory terms control."
      },
      {
        "type": "h",
        "text": "13. Return and Deletion"
      },
      {
        "type": "p",
        "text": "During the term, the Organization may exercise available Service functionality to access, export, correct, or delete Customer Data."
      },
      {
        "type": "p",
        "text": "Upon termination, the Organization may request export of Customer Data during the period specified in the Terms."
      },
      {
        "type": "p",
        "text": "After expiration of the applicable export or reactivation period, ConnectYid will delete or de-identify Organization-controlled Personal Data unless Applicable Data Protection Law requires continued retention."
      },
      {
        "type": "p",
        "text": "Residual backup copies may remain until overwritten or deleted in accordance with ConnectYid’s ordinary backup cycle, provided they remain protected and are not returned to active Processing except as necessary for restoration, security, or legal compliance."
      },
      {
        "type": "h",
        "text": "14. Liability"
      },
      {
        "type": "p",
        "text": "The disclaimers, exclusions, limitations of liability, and indemnification provisions contained in the Terms apply to this DPA to the maximum extent permitted by Applicable Data Protection Law."
      },
      {
        "type": "p",
        "text": "Nothing in this DPA limits or excludes liability to the extent such liability cannot lawfully be limited or excluded."
      },
      {
        "type": "h",
        "text": "15. Order of Precedence and Term"
      },
      {
        "type": "p",
        "text": "This DPA takes effect when accepted by the Organization and remains effective for so long as ConnectYid Processes Personal Data on behalf of the Organization."
      },
      {
        "type": "p",
        "text": "In the event of conflict concerning Processing subject to this DPA, the following order of precedence applies:"
      },
      {
        "type": "li",
        "text": "mandatory terms of an applicable EU SCC, UK transfer mechanism, or other legally required transfer instrument;"
      },
      {
        "type": "li",
        "text": "this DPA;"
      },
      {
        "type": "li",
        "text": "the Privacy Policy;"
      },
      {
        "type": "li",
        "text": "the Terms."
      },
      {
        "type": "p",
        "text": "All provisions that by their nature must survive termination, including confidentiality, deletion, audit, international-transfer, and liability provisions, survive for the period necessary to fulfill their purpose."
      },
      {
        "type": "h",
        "text": "16. Organization Representation and Electronic Acceptance"
      },
      {
        "type": "p",
        "text": "By accepting this DPA, the Organization represents, warrants, and agrees that:"
      },
      {
        "type": "p",
        "text": "(a) the individual accepting this DPA has authority to bind the Organization;"
      },
      {
        "type": "p",
        "text": "(b) the Organization has established and will maintain an appropriate lawful basis under Article 6 or other applicable law for Personal Data it instructs ConnectYid to Process;"
      },
      {
        "type": "p",
        "text": "(c) where Special Category Data is Processed, the Organization has established and will maintain an applicable Article 9 condition or corresponding UK GDPR condition;"
      },
      {
        "type": "p",
        "text": "(d) the Organization has provided or will provide all legally required notices;"
      },
      {
        "type": "p",
        "text": "(e) the Organization has obtained and will maintain records of legally required consents, permissions, or other authorizations;"
      },
      {
        "type": "p",
        "text": "(f) the Organization will maintain appropriate procedures for honoring Data Subject rights;"
      },
      {
        "type": "p",
        "text": "(g) the Organization has authority to disclose Customer Data to ConnectYid and instruct ConnectYid to Process it;"
      },
      {
        "type": "p",
        "text": "(h) the Organization will use reasonable measures to keep Customer Data accurate, relevant, and limited to what is necessary for authorized purposes;"
      },
      {
        "type": "p",
        "text": "(i) the Organization will not instruct ConnectYid to Process Personal Data in violation of Applicable Data Protection Law; and"
      },
      {
        "type": "p",
        "text": "(j) the Organization has reviewed and accepts this DPA, the Terms, and the Privacy Policy."
      },
      {
        "type": "p",
        "text": "Electronic acceptance, including clicking an “I Accept,” “Agree,” or substantially similar button associated with this DPA, constitutes execution of this DPA by the Organization."
      },
      {
        "type": "p",
        "text": "TELUKTU INC. d/b/a CONNECTYID\n221 Paseo del Pueblo Norte\nTaos, New Mexico 87571\nPrivacy Contact: connectyid1@gmail.com"
      },
      {
        "type": "p",
        "text": "Organization: Identified through the Organization’s ConnectYid registration/account records."
      },
      {
        "type": "p",
        "text": "Execution: Electronic acceptance recorded by ConnectYid, including the accepting user, Organization, date/time, and version of this DPA."
      }
    ]
  }
};
